import {
  DEAD_SESSION_CODES,
  LEGACY_DEAD_SESSION_TEXTS,
  isRefreshUnavailableError,
  isSessionChangedError,
  isSessionEndedError,
} from '../gql/refreshFailure';
import { esErrorTransitorioDeBackend } from '../gql/retryableBackendCodes';

/**
 * Clasifica el error de una mutation reproducida desde una cola offline para
 * decidir qué hacer con ella.
 *
 * BUG QUE CORRIGE (pérdida de datos): el sync offline trataba CUALQUIER
 * `graphQLErrors` como "poison pill" y la BORRABA de la cola (removeFromQueue
 * permanente). Pero `'Empty AccessToken'` / `'Refresh token expire'` se emiten
 * como GraphQLError cuando el token caduca (típico al reconectar tras estar sin
 * red): la mutation era VÁLIDA, solo faltaba re-autenticar → se descartaba el
 * trabajo del operario para siempre.
 *
 * Reglas:
 *  - 'auth'       → sesión caducada/sin token. NO descartar: mantener en cola y
 *                   pedir re-login; se reintenta tras autenticar.

 'validation' → poison pill REAL (400 / GraphQLError de validación no-auth).
 *                   Descartar para no entrar en bucle infinito.
 *  - 'transient'  → red/5xx/offline. Mantener en cola y reintentar.
 */
/**
 * Conflictos 409 de NEGOCIO que no se resuelven reintentando.
 *
 * Un 409 es transitorio por defecto (carrera de la clave de idempotencia,
 * SAVE_TX_CONFLICT), pero hay códigos con significado propio: el estado del
 * servidor ya no admite la operación y volver a enviarla dará siempre lo mismo.
 * Sin esta lista la cola principal (que no tiene tope de reintentos) la
 * reintentaría en cada sync para siempre.
 *  - SR_SEGMENT_ALREADY_LINKED: el tramo ya está vinculado a otra OT (Sala lo
 *    vinculó mientras la tablet estaba sin red).
 * Solo se añaden códigos cuyo reintento NUNCA puede salir bien.
 */
const PERMANENT_CONFLICT_CODES: readonly string[] = ['SR_SEGMENT_ALREADY_LINKED'];

export type OfflineErrorKind = 'auth' | 'validation' | 'transient';

export function classifyOfflineError(error: unknown): OfflineErrorKind {
  const e = error as {
    message?: string;
    graphQLErrors?: Array<{ message?: string; extensions?: { code?: string } }>;
    networkError?: {
      statusCode?: number;
      message?: string;
      result?: { errors?: unknown[] };
    };
  };

  const gqlErrors = e?.graphQLErrors ?? [];
  const messages = [
    e?.message,
    e?.networkError?.message,
    ...gqlErrors.map((g) => g?.message),
  ].filter((m): m is string => typeof m === 'string');

  // Spec 2026-10-01 § 6.4: lo que decidió la puerta única del refresco manda.
  // Una sesión muerta no dice nada de la OPERACIÓN: se conserva y se pide login.
  if (isSessionEndedError(error)) return 'auth';
  if (isRefreshUnavailableError(error)) return 'transient';
  // La operación del replay ya va sellada con su dueño: se conserva sin gastar
  // intentos y se enviará cuando vuelva (spec § 12.1).
  if (isSessionChangedError(error)) return 'transient';

  // AUTH: token vacío/expirado o sesión muerta en el servidor. Detección PRECISA
  // (no todo FORBIDDEN: un FORBIDDEN de RBAC sí es permanente y debe descartarse
  // como validación). «User not found or inactive» y «no active tenant» se
  // DESCARTABAN como veneno hasta el 2026-10-01: borraban el trabajo del operario.
  const hasUnauthCode = gqlErrors.some(
    (g) => g?.extensions?.code === 'UNAUTHENTICATED',
  );
  const hasDeadSessionCode = gqlErrors.some((g) => {
    const code = (g?.extensions as { originalError?: { code?: string } } | undefined)
      ?.originalError?.code;
    return typeof code === 'string' && (DEAD_SESSION_CODES as readonly string[]).includes(code);
  });
  // Los textos de transición salen de `refreshFailure` (una sola fuente).
  const escapeRegex = (t: string) => t.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const authPhrase = new RegExp(
    ['empty\\s*accesstoken', 'not authenticated', ...LEGACY_DEAD_SESSION_TEXTS.map(escapeRegex)].join('|'),
    'i',
  );
  const isAuth =
    hasUnauthCode ||
    hasDeadSessionCode ||
    e?.networkError?.statusCode === 401 ||
    messages.some((m) => authPhrase.test(m));
  if (isAuth) return 'auth';

  // TRANSITORIO DEL BACKEND (paridad con la cola de fotos, 2026-08-10): el
  // contrato de SAVE_TX_TIMEOUT / SAVE_TX_CONFLICT / DB_TRANSIENT_ERROR es
  // «rollback total, reintentar es seguro». Y un 409 sin código es la carrera
  // de la clave de idempotencia: la petición abortada por el techo de espera
  // aún corría en el servidor cuando la cola la reprodujo. Ninguno es veneno;
  // la siguiente pasada converge. Antes (2026-09-08) los dos se DESCARTABAN
  // como validación por traer graphQLErrors.
  const statuses = [
    e?.networkError?.statusCode,
    ...gqlErrors.map(
      (g) => (g?.extensions as { status?: number } | undefined)?.status,
    ),
    ...gqlErrors.map(
      (g) =>
        (g?.extensions as { originalError?: { statusCode?: number } } | undefined)
          ?.originalError?.statusCode,
    ),
  ];
  const hasPermanentConflict = gqlErrors.some((g) => {
    const code = (g?.extensions as { originalError?: { code?: string } } | undefined)
      ?.originalError?.code;
    return typeof code === 'string' && PERMANENT_CONFLICT_CODES.includes(code);
  });
  if (hasPermanentConflict) return 'validation';
  if (esErrorTransitorioDeBackend(error) || statuses.includes(409)) {
    return 'transient';
  }

  // VALIDATION (poison pill real): GraphQLError no-auth o 400.
  const isValidation =
    gqlErrors.length > 0 ||
    e?.networkError?.statusCode === 400 ||
    (Array.isArray(e?.networkError?.result?.errors) &&
      e.networkError.result.errors.length > 0);
  if (isValidation) return 'validation';

  return 'transient';
}
